import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import Modal from './Modal';
import Button from '../Form/Button';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileImport, faXmark } from '@fortawesome/free-solid-svg-icons';
import { API_BASE_URL } from '../../api';
import '../Form/Fields/ImageUploader.scss';

// Upload only — the extraction/review workflow lives on the purchase order's own detail page,
// which this redirects to as soon as the file is accepted (analysis keeps running there).
export default function ImportPurchaseOrderModal({ show, onClose }) {
    const navigate = useNavigate();
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);

    const onDrop = useCallback((acceptedFiles) => {
        const picked = acceptedFiles[0];
        if (!picked) return;
        setFile(Object.assign(picked, { preview: URL.createObjectURL(picked) }));
    }, []);

    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
        onDrop,
        accept: { 'image/*': [] },
        multiple: false,
        noClick: true,
        noKeyboard: true,
    });

    const handleClose = () => {
        setFile(null);
        setUploading(false);
        onClose();
    };

    const handleSubmit = async () => {
        if (!file) {
            toast.warning('Sélectionnez la photo ou le scan du bon d\'achat');
            return;
        }
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const { data } = await axios.post(`${API_BASE_URL}/api/v1/purchase-order-imports`, formData, { withCredentials: true });
            setFile(null);
            setUploading(false);
            onClose();
            navigate(`/purchase-order-imports/${data.id}`);
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Erreur lors de l\'import du bon d\'achat');
            setUploading(false);
        }
    };

    return (
        <Modal
            id="importPurchaseOrderModal"
            title="Importer un bon d'achat"
            show={show}
            onClose={handleClose}
            icon={faFileImport}
            iconBg="#EAF7F2"
            iconColor="#0F6E56"
            footer={
                <Button
                    isLoading={uploading}
                    loadingMessage="Envoi…"
                    className="btn btn-primary w-100"
                    type="button"
                    onClick={handleSubmit}
                    disabled={!file}
                >
                    Importer
                </Button>
            }
        >
            <p style={{ color: '#6b7280', fontSize: 13, marginBottom: 16 }}>
                Prenez en photo ou scannez le bon d'achat du fournisseur. Les lignes du bon seront extraites automatiquement par IA.
            </p>

            <div className="image-uploader">
                <div {...getRootProps({ className: 'dropzone' })}>
                    <input {...getInputProps()} />
                    <div className={`dropzone-area${isDragActive ? ' active' : ''}`}>
                        <div className="dropzone-icon">
                            <FontAwesomeIcon icon={faFileImport} />
                        </div>
                        <p>{isDragActive ? 'Déposez le fichier ici...' : 'Glissez-déposez une image, ou'}</p>
                        <button className="dropzone-btn" type="button" onClick={open}>
                            <FontAwesomeIcon icon={faFileImport} />
                            Choisir une image
                        </button>
                    </div>
                </div>

                {file && (
                    <div className="previews">
                        <div className="preview">
                            <img src={file.preview} alt={file.name} />
                            <button
                                type="button"
                                className="preview-delete"
                                onClick={() => setFile(null)}
                                title="Retirer"
                            >
                                <FontAwesomeIcon icon={faXmark} />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
}
